import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { sendToGoogleSheets } from '../utils/googleSheetsApi';

const { FiMail, FiUser, FiX, FiCheck, FiLoader, FiLock, FiInbox } = FiIcons;

const EmailCaptureModal = ({ isOpen, onClose, onSubmit, calculatorType, previewData }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Calculate preview amounts based on calculator type
  const previewAmount = previewData?.threeYearValue || 0;
  const formattedPreviewAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(previewAmount);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic email validation
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Get the stored calculator data
      const localStorageKey = `calculator_${calculatorType === 'branding' ? 'brandData' : 'data'}`;
      const storedData = window.localStorage.getItem(localStorageKey);
      const inputValues = storedData ? JSON.parse(storedData) : {};

      // First send data to Google Sheets - this will generate the report URL
      const generatedReportUrl = await sendToGoogleSheets({
        email,
        firstName,
        calculatorType,
        inputValues,
        results: previewData || {},
      });

      // Then call the original onSubmit handler with the report URL
      await onSubmit({ email, firstName, reportUrl: generatedReportUrl });
      
      setSuccess(true);
      // Modal will NOT be closed automatically - user must click close button
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-wow-gradient text-white p-6 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
              aria-label="Close modal"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
            <h3 className="text-2xl font-bold mb-2">Unlock Your Complete ROI Analysis</h3>
            <p className="text-white/80">
              Your organization could be leaving up to {formattedPreviewAmount} on the table
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              <div className="text-center py-6">
                <div className="bg-green-100 text-green-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheck} className="text-3xl" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Success!</h4>
                <p className="text-gray-600 mb-4">
                  Your detailed ROI analysis has been sent to your email.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg mb-4 flex items-start">
                  <SafeIcon icon={FiInbox} className="text-wow-blue mt-1 mr-3 flex-shrink-0" />
                  <div className="text-left text-sm text-blue-700">
                    <strong>Important:</strong> Please check your inbox (and spam folder) for an email from Wow Digital with your report link. The report contains all unlocked features and comprehensive analysis.
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="bg-wow-gradient hover:shadow-lg hover:-translate-y-1 transition-all text-white font-medium px-6 py-2 rounded-lg"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Enter your details below to receive your personalized ROI analysis report with actionable recommendations.
                  </p>
                  <div className="flex items-center text-gray-500 bg-gray-50 p-3 rounded-lg text-sm">
                    <SafeIcon icon={FiLock} className="mr-2 text-wow-blue" />
                    <span>Your information is secure and will never be shared with third parties.</span>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SafeIcon icon={FiUser} className="text-gray-400" />
                        </div>
                        <input
                          id="firstName"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wow-blue focus:border-wow-blue"
                          placeholder="Your first name"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SafeIcon icon={FiMail} className="text-gray-400" />
                        </div>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wow-blue focus:border-wow-blue"
                          placeholder="you@organization.org"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-wow-gradient hover:shadow-lg disabled:opacity-70 text-white font-medium px-6 py-3 rounded-lg transition-all flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        'Get My Complete Analysis'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-4 text-center text-xs text-gray-500">
                  By submitting, you agree to our <a href="#" className="text-wow-blue hover:underline">privacy policy</a>.
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailCaptureModal;